import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, LoaderCircle, X } from 'lucide-react';
import { PxlKitIcon } from '@pxlkit/core';
import type { PxlKitData } from '@pxlkit/core';
import { gameIcons, legacyAvatarIcons } from './pixelIcons';
import type { TeamIcon } from '../shared/constants';

export function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? 'brand--compact' : ''}`}>
      <span>RAVE</span> CAVE
    </div>
  );
}

export function Shell({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return (
    <main className={`shell ${className}`}>
      <div className="fog fog--one" />
      <div className="fog fog--two" />
      {children}
    </main>
  );
}

export function Card({
  children,
  className = '',
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLElement>>) {
  return (
    <section className={`card ${className}`} {...props}>
      {children}
    </section>
  );
}

export function Button({
  children,
  variant = 'primary',
  busy,
  ...props
}: PropsWithChildren<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
    busy?: boolean;
  }
>) {
  return (
    // Spread first so an explicit `disabled={false}` cannot re-enable a busy button.
    <button {...props} className={`button button--${variant}`} disabled={busy || props.disabled}>
      {busy ? <LoaderCircle size={18} className="spin" /> : null}
      {children}
    </button>
  );
}

export function Notice({
  type = 'info',
  children,
  onClose,
}: {
  type?: 'info' | 'error' | 'success';
  children: ReactNode;
  onClose?: () => void;
}) {
  const Icon = type === 'error' ? AlertTriangle : type === 'success' ? CheckCircle2 : null;
  return (
    <div className={`notice notice--${type}`}>
      {Icon && <Icon size={18} />}
      <span>{children}</span>
      {onClose && (
        <button onClick={onClose} aria-label="Dismiss">
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: PropsWithChildren) {
  return <div className="empty">{children}</div>;
}

/** Renders a pixel icon at the surrounding font size (1em), like the emoji it replaced. */
export function PixelIcon({
  icon,
  label,
  tint,
  className = '',
}: {
  icon: PxlKitData;
  label?: string;
  tint?: string;
  className?: string;
}) {
  return (
    <PxlKitIcon
      icon={icon}
      appearance={tint ? 'tinted' : 'palette'}
      color={tint}
      aria-label={label}
      className={`pixel-icon ${className}`}
      style={{ width: '1em', height: '1em' }}
    />
  );
}

/** A team or player icon by its stored key; unknown keys fall back to the ghost. */
export function GameIcon({ name, className }: { name?: string | null; className?: string }) {
  const icon = gameIcons[name as TeamIcon] ?? legacyAvatarIcons[name ?? ''] ?? gameIcons.ghost;
  return <PixelIcon icon={icon} label={name ?? 'ghost'} className={className} />;
}
